import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CONTEUDO_CHAVES } from '@/api/adminApi'
import {
  canAccessAgendamentoPage,
  fetchAgendamentoFlags,
  resolveAgendamentoFlagsFromRaw,
  type AgendamentoFlags,
} from '@/utils/agendamentoFlags'
import { readConteudoSnapshot, subscribeConteudoUpdated } from '@/utils/conteudoSync'

const DEFAULT: AgendamentoFlags = { fluxo: true, historico: true, servicosMarcacao: true }

function flagsFromSnapshot(): AgendamentoFlags | null {
  const snap = readConteudoSnapshot()
  if (
    !(CONTEUDO_CHAVES.agendamento_fluxo_enabled in snap) &&
    !(CONTEUDO_CHAVES.historico_cliente_enabled in snap) &&
    !(CONTEUDO_CHAVES.servicos_marcacao_enabled in snap)
  ) {
    return null
  }
  return resolveAgendamentoFlagsFromRaw(
    snap[CONTEUDO_CHAVES.agendamento_fluxo_enabled] ?? null,
    snap[CONTEUDO_CHAVES.historico_cliente_enabled] ?? null,
    snap[CONTEUDO_CHAVES.servicos_marcacao_enabled] ?? null,
    snap[CONTEUDO_CHAVES.agendamentos_enabled] ?? null
  )
}

export function useAgendamentoFlags() {
  const initial = flagsFromSnapshot()
  const [flags, setFlags] = useState<AgendamentoFlags>(initial ?? DEFAULT)
  const [loading, setLoading] = useState(initial === null)
  const hasLoaded = useRef(initial !== null)

  const load = useCallback(
    (silent = false) => {
      if (!silent) setLoading(true)
      return fetchAgendamentoFlags()
        .then(setFlags)
        .catch(() => {
          if (!hasLoaded.current) setFlags(DEFAULT)
        })
        .finally(() => {
          hasLoaded.current = true
          setLoading(false)
        })
    },
    []
  )

  useEffect(() => {
    let cancelled = false
    const run = () => {
      if (cancelled) return
      const fromSnap = flagsFromSnapshot()
      if (fromSnap) setFlags(fromSnap)
      void load(hasLoaded.current)
    }
    run()
    const unsub = subscribeConteudoUpdated(run)
    return () => {
      cancelled = true
      unsub()
    }
  }, [load])

  return { flags, loading }
}

export function useRequireAgendamentoPage(redirectTo = '/menu') {
  const navigate = useNavigate()
  const { flags, loading } = useAgendamentoFlags()

  useEffect(() => {
    if (!loading && !canAccessAgendamentoPage(flags)) {
      navigate(redirectTo, { replace: true })
    }
  }, [loading, flags, navigate, redirectTo])

  return { flags, loading, allowed: canAccessAgendamentoPage(flags) }
}

export function useRequireHistoricoCliente(redirectTo = '/menu') {
  const navigate = useNavigate()
  const { flags, loading } = useAgendamentoFlags()

  useEffect(() => {
    if (!loading && !flags.historico) navigate(redirectTo, { replace: true })
  }, [loading, flags.historico, navigate, redirectTo])

  return { loading }
}
